import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { loadCart, removeFromCart, setCartQty } from '../common/shopUtils';
import { formatMoney, getEffectiveProductPricing } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

export default function ShopCart() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, cartScopeKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});
  const shop = websiteContent.shop || {};
  const shopEnabled = shop.enabled !== false;
  const currency = shop.currency || 'USD';
  const campaigns = useMemo(() => (Array.isArray(shop.campaigns) ? shop.campaigns : []), [shop.campaigns]);
  const products = useMemo(() => {
    const list = Array.isArray(shop.products) ? shop.products : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.products, i18n.language]);

  const [cart, setCart] = useState(() => loadCart(cartScopeKey));

  const lines = useMemo(() => {
    return Object.entries(cart || {})
      .map(([productId, qty]) => {
        const product = products.find((p) => p && p.id === productId);
        if (!product || product.published === false) return null;
        const safeQty = Math.max(1, Number(qty || 0));
        const pricing = getEffectiveProductPricing(product, campaigns);
        const price = Number(pricing.finalPrice || 0);
        const savings = Number(pricing.savings || 0) * safeQty;
        const image = product.image || (Array.isArray(product.images) && product.images.length ? product.images[0] : '');
        return {
          productId,
          qty: safeQty,
          title: product.title,
          description: product.description,
          image,
          price,
          subtotal: price * safeQty,
          savings,
        };
      })
      .filter(Boolean);
  }, [cart, products, campaigns]);

  const total = lines.reduce((sum, l) => sum + Number(l.subtotal || 0), 0);
  const totalSavings = lines.reduce((sum, l) => sum + Number(l.savings || 0), 0);

  if (partner?.inactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (partner && isPartnerComingSoon(partner) && !isAdmin) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (partner && isPartnerMaintenance(partner) && !isAdmin) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const updateQty = (productId, qty) => {
    const next = setCartQty(productId, qty, cartScopeKey);
    setCart(next);
  };

  const removeItem = (productId) => {
    const next = removeFromCart(productId, cartScopeKey);
    setCart(next);
  };

  return (
    <PublicLayout title={t('cart') || 'Cart'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: { xs: 34, md: 44 }, letterSpacing: -1 }}>
          {t('cart') || 'Cart'}
        </Typography>
        <Typography sx={{ color: theme.muted, mt: 1, mb: 4 }}>
          {t('cart_subtitle') || 'Review your items before checkout.'}
        </Typography>

        {!shopEnabled ? (
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}
          >
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('shop_disabled') || 'Shop is disabled'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('shop_disabled_help') || 'Please check back later.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(paths.home)}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
            >
              {t('home') || 'Home'}
            </Button>
          </Paper>
        ) : lines.length ? (
          <Stack spacing={2}>
            {lines.map((line) => (
              <Paper
                key={line.productId}
                elevation={0}
                sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.cardBg }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                    {line.image ? (
                      <Box
                        component="img"
                        alt=""
                        src={line.image}
                        sx={{
                          width: 78,
                          height: 58,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: `1px solid ${theme.border}`,
                          bgcolor: theme.sectionAltBg,
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: theme.text }}>{line.title}</Typography>
                      <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
                        {line.description}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      type="number"
                      label={t('qty') || 'Qty'}
                      value={line.qty}
                      onChange={(e) => updateQty(line.productId, e.target.value)}
                      inputProps={{ min: 1, style: { width: 80 } }}
                    />
                    <Typography sx={{ minWidth: 120, textAlign: 'right', fontWeight: 900, color: theme.text }}>
                      {formatMoney(line.subtotal, currency)}
                    </Typography>
                    <IconButton color="error" onClick={() => removeItem(line.productId)}>
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Stack>
                </Stack>
                {line.savings ? (
                  <Typography variant="body2" sx={{ color: theme.muted, mt: 1 }}>
                    {(t('you_saved') || 'You saved') + ' ' + formatMoney(line.savings, currency)}
                  </Typography>
                ) : null}
              </Paper>
            ))}

            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}
            >
              <Stack spacing={1.5}>
                {totalSavings ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: theme.muted }}>{t('savings') || 'Savings'}</Typography>
                    <Typography sx={{ fontWeight: 900, color: theme.text }}>
                      {formatMoney(totalSavings, currency)}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: theme.muted }}>{t('total') || 'Total'}</Typography>
                  <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 18 }}>
                    {formatMoney(total, currency)}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(paths.shop)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('continue_shopping') || 'Continue shopping'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate(paths.checkout)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      bgcolor: theme.accent || '#111827',
                      '&:hover': { bgcolor: theme.accent || '#0b1224' },
                    }}
                  >
                    {t('checkout') || 'Checkout'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}
          >
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('cart_empty') || 'Your cart is empty'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('cart_empty_help') || 'Browse the shop and add items to get started.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(paths.shop)}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
            >
              {t('go_to_shop') || 'Go to shop'}
            </Button>
          </Paper>
        )}
      </Box>
    </PublicLayout>
  );
}
